// FollowButton — toggle follow/unfollow with optimistic update.
// PRO gating (Phase 2): if the backend returns 403 with `PRO_REQUIRED` the
// button silently redirects to /pricing instead of swallowing the error.
import React, { useState } from "react";
import axios from "axios";
import { useLanguage } from "../../i18n/LanguageContext";
import { tShadow } from "../../i18n/shadow-social";
import { useAuth } from "../../contexts/AuthContext";
import { isPro } from "../../lib/shadow-permissions";
// Runtime-first API base — same fix as the rest of Shadow Network. Using
// REACT_APP_BACKEND_URL would make the follow request fail silently in
// production because of cross-origin + credentials preflight.
import { API_BASE as API } from "../../lib/api";

// Trigger the Stripe checkout flow used everywhere else (mirrors the call in
// SimulatorPage). Returns nothing — on success it navigates the browser to
// Stripe via `window.location.href`.
const startProCheckout = async () => {
  try {
    const token = localStorage.getItem("rebel_jwt");
    const res = await fetch(`${API}/api/payments/checkout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        plan: "pro_lifetime",
        origin_url: window.location.origin,
      }),
    });
    const data = await res.clone().json().catch(() => ({}));
    if (data?.url) window.location.href = data.url;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[FollowButton] checkout failed:", e);
  }
};

export const FollowButton = ({
  targetUserId,
  initialFollowing = false,
  onFollowChange,
  size = "md",
  className = "",
}) => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [following, setFollowing] = useState(initialFollowing);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(false);
  const userIsPro = isPro(user);

  const handleToggle = async () => {
    if (loading) return;
    // No session → send to login
    if (!user) {
      window.location.href = "/login";
      return;
    }
    // FREE → straight to Stripe checkout
    if (!userIsPro) {
      setLoading(true);
      await startProCheckout();
      setLoading(false);
      return;
    }
    const next = !following;
    setFollowing(next); // optimistic
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/shadow/social/follow`,
        { target_user_id: targetUserId }
      );
      setFollowing(!!data.following);
      onFollowChange?.(data.following, data.followers_count);
    } catch (e) {
      setFollowing(!next); // rollback
      const detail = e?.response?.data?.detail;
      if (
        e?.response?.status === 403 &&
        ((typeof detail === "object" && detail?.error === "PRO_REQUIRED") ||
          (typeof detail === "string" && detail.toUpperCase().includes("PRO")))
      ) {
        await startProCheckout();
      }
    } finally {
      setLoading(false);
    }
  };

  const padding = size === "sm" ? "px-3 py-1 text-[10px]" : "px-4 py-1.5 text-xs";

  // FREE users see "Seguir 🔒" — clearly invitational but not blocking.
  const label = !userIsPro
    ? `🔒 ${tShadow("seguir", lang)}`
    : !following
    ? tShadow("seguir", lang)
    : hover
    ? tShadow("dejarSeguir", lang)
    : tShadow("siguiendo", lang);

  return (
    <button
      type="button"
      onClick={handleToggle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={loading}
      data-testid={`follow-btn-${targetUserId}`}
      data-following={following ? "true" : "false"}
      data-pro={userIsPro ? "true" : "false"}
      title={!userIsPro ? "Seguir requiere PRO" : undefined}
      className={`inline-flex items-center justify-center rounded-full font-bold uppercase tracking-[0.15em] transition-all disabled:opacity-60 ${padding} ${className}`}
      style={{
        background: !following
          ? "linear-gradient(135deg, #f5a623, #e8941a)"
          : hover
          ? "rgba(255, 51, 85, 0.12)"
          : "transparent",
        color: !following ? "#07080f" : hover ? "#ff5577" : "#ffffff",
        border: !following
          ? "none"
          : hover
          ? "1px solid rgba(255, 51, 85, 0.5)"
          : "1px solid rgba(255, 255, 255, 0.25)",
        boxShadow: !following ? "0 2px 12px rgba(245, 166, 35, 0.3)" : "none",
        minWidth: size === "sm" ? 80 : 110,
      }}
    >
      {label}
    </button>
  );
};

export default FollowButton;
